import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import { msUntil } from '../../common/clock';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

/**
 * The live question: prompt, options, timer and the post-window reveal.
 *
 * Draws its countdown from the server-supplied window rather than from a local
 * stopwatch, so a refresh, a reconnect or a backgrounded tab all resume at the
 * same instant everyone else is at.
 */
export default class QuestionView extends Component {
  view() {
    const state = this.attrs.state;
    const question = state.question;

    if (!question) {
      return (
        <div className="HappyQuiz-status" role="status">
          {app.translator.trans('happydigital-quiz.forum.live.waiting_next')}
        </div>
      );
    }

    const live = state.live.state;
    const revealing = !!live.isRevealing;
    const total = state.contest.questionCount;
    const current = live.questionIndex + 1;

    return (
      <div>
        <div className="HappyQuiz-topline">
          <span>
            {app.translator.trans('happydigital-quiz.forum.live.counter')}{' '}
            <b>
              {app.translator.trans('happydigital-quiz.forum.live.counter_value', {
                current,
                total,
              })}
            </b>
          </span>
          {this.timer(live, revealing)}
        </div>

        <div className="HappyQuiz-track">
          <div className="HappyQuiz-fill" style={{ width: `${this.progressPercent(live, revealing)}%` }} />
        </div>

        {this.steps(state, live)}

        {question.category ? <div className="HappyQuiz-eyebrow">{question.category}</div> : null}

        {question.imageUrl ? (
          <div className="HappyQuiz-questionImage">
            <img src={question.imageUrl} alt="" />
          </div>
        ) : null}

        <h2 className="HappyQuiz-question" tabindex="-1">
          {question.prompt}
        </h2>

        <div className="HappyQuiz-options">{question.options.map((option, i) => this.option(state, option, i, revealing))}</div>

        {this.jokerButton(state, revealing)}

        {this.statusLine(state)}
      </div>
    );
  }

  /**
   * Counts down the answering window, then the reveal.
   */
  timer(live, revealing) {
    const target = revealing ? live.revealEndsAt : live.questionClosesAt;
    const remaining = Math.ceil(msUntil(target) / 1000);

    // Under five seconds the timer turns red, matching the reference design.
    const urgent = !revealing && remaining <= 5;

    return (
      <b className={'HappyQuiz-timer' + (urgent ? ' is-urgent' : '')} role="timer" aria-live="off">
        {app.translator.trans('happydigital-quiz.forum.live.seconds', { count: remaining })}
      </b>
    );
  }

  progressPercent(live, revealing) {
    const start = revealing ? live.questionClosesAt : live.questionOpensAt;
    const end = revealing ? live.revealEndsAt : live.questionClosesAt;

    const span = Date.parse(end) - Date.parse(start);

    if (!span || Number.isNaN(span)) return 0;

    return Math.max(0, Math.min(100, (msUntil(end) / span) * 100));
  }

  /**
   * One bar per question, marking only which one is current. Past questions
   * no longer carry correct/incorrect colouring — that feedback is not shown
   * to the player at all now.
   */
  steps(state, live) {
    const total = state.contest.questionCount;

    const bars = [];

    for (let i = 0; i < total; i++) {
      bars.push(<span className={'HappyQuiz-step' + (i === live.questionIndex ? ' is-active' : '')} />);
    }

    return (
      <div className="HappyQuiz-steps" aria-hidden="true">
        {bars}
      </div>
    );
  }

  option(state, option, index, revealing) {
    const myAnswer = state.live.myAnswer;
    const selected = myAnswer?.selectedOptionId;
    const pending = state.pendingOptionId;
    const hidden = state.live.myJoker?.hiddenOptionIds || [];
    const eliminated = hidden.includes(option.id);

    let cls = '';
    let ariaLabel = option.text;

    // Which option was picked is shown, but never whether it was right — the
    // reveal window still runs on the server for timing, but the correct/
    // incorrect colouring and mark are not surfaced to the player.
    if (option.id === selected || (state.submitting && option.id === pending)) {
      cls = ' is-pending';
    }

    if (eliminated) {
      cls += ' is-eliminated';
      ariaLabel = `${option.text}, ${app.translator.trans('happydigital-quiz.forum.live.joker_eliminated')}`;
    }

    // Locked once an answer is accepted, while one is in flight, during the
    // reveal, eliminated by the joker, or for viewers who are not registered
    // participants.
    const disabled = revealing || !!selected || state.submitting || eliminated || !state.canAnswer;

    return (
      <button
        type="button"
        className={'HappyQuiz-option' + cls}
        disabled={disabled}
        aria-label={ariaLabel}
        onclick={() => state.onanswer(option.id)}
      >
        <span className="HappyQuiz-letter">{LETTERS[index] || index + 1}</span>
        <span className="HappyQuiz-optionText">{option.text}</span>
      </button>
    );
  }

  /**
   * The 50/50: hides two of the wrong options for this question only.
   *
   * Hidden whenever it would have nothing to do — no jokers left, already
   * used on this question, already answered, or the window has closed —
   * rather than shown disabled, since a disabled button with no live reason
   * attached to it is more confusing than no button at all.
   */
  jokerButton(state, revealing) {
    const myAnswer = state.live.myAnswer;
    const myJoker = state.live.myJoker;
    const remaining = state.live.jokersRemaining || 0;

    if (!state.canAnswer || revealing || myAnswer || myJoker || remaining < 1) {
      return null;
    }

    return (
      <div className="HappyQuiz-jokerRow">
        <button
          type="button"
          className="HappyQuiz-joker"
          disabled={state.jokerPending}
          onclick={() => state.onjoker()}
        >
          <i className="fas fa-bolt" aria-hidden="true" />
          {app.translator.trans('happydigital-quiz.forum.live.joker_use', { count: remaining })}
        </button>

        {state.jokerFailed ? (
          <span className="HappyQuiz-jokerError">
            {app.translator.trans('happydigital-quiz.forum.live.joker_failed')}
          </span>
        ) : null}
      </div>
    );
  }

  /**
   * The submission lifecycle, announced to screen readers as it changes.
   */
  statusLine(state) {
    let message = null;

    if (!state.canAnswer) {
      message = app.translator.trans('happydigital-quiz.forum.live.watching');
    } else if (state.submitting) {
      message = app.translator.trans('happydigital-quiz.forum.live.submitting');
    } else if (state.submitFailed) {
      message = [
        app.translator.trans('happydigital-quiz.forum.live.submit_failed'),
        ' ',
        // Retrying is offered only while the window is still open.
        msUntil(state.live.state.questionClosesAt) > 0 ? (
          <button type="button" className="Button Button--text" onclick={() => state.onretry()}>
            {app.translator.trans('happydigital-quiz.forum.live.retry')}
          </button>
        ) : null,
      ];
    } else if (state.live.myAnswer) {
      // Shown through the reveal window too now that it no longer surfaces
      // correct/incorrect: an answered question should never go silent.
      message = app.translator.trans('happydigital-quiz.forum.live.submitted');
    }

    if (!message) return null;

    return (
      <div className="HappyQuiz-status" role="status">
        {message}
      </div>
    );
  }

}
